import { fbt } from 'fbtee'
import invariant from 'tiny-invariant'
import { Ticket } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Skeleton } from '../../../components/ui/skeleton'
import { UserAvatar } from '../../../components/UserAvatar'
import { friendFallbackName } from '../../../lib/commonStrings'
import type { UserProfile } from '../../../types'

/** Someone in the crew put the viewer's name on a ticket — they confirm or pass. */
export function TicketAssignmentCard({ loading, owner, ownerId, busy, onAccept, onDecline }: {
  loading?: boolean
  owner?: UserProfile
  ownerId?: string
  busy?: boolean
  onAccept?: () => void
  onDecline?: () => void
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-3">
        <Skeleton className="h-4 w-44" />
        <Skeleton className="h-7 w-32" />
      </div>
    )
  }

  invariant(ownerId, 'TicketAssignmentCard: ownerId is required when not loading')
  invariant(onAccept, 'TicketAssignmentCard: onAccept is required when not loading')
  invariant(onDecline, 'TicketAssignmentCard: onDecline is required when not loading')
  const name = owner?.displayName?.trim() || friendFallbackName()

  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm" data-testid="ticket-assignment-card">
      <div className="flex items-center gap-2.5">
        <UserAvatar profile={owner} fallbackName={name} size="sm" />
        <span className="min-w-0 flex-1 font-medium">
          {fbt(fbt.param('name', name) + ' has a ticket for you', 'Card title when someone in the crew assigned one of their tickets to the viewer')}
        </span>
        <Ticket className="size-4 shrink-0 text-primary" />
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" disabled={busy} onClick={onAccept}>
          {fbt("Yes, I'm going", 'Button accepting a ticket someone assigned to the viewer, which also marks them going')}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onDecline}>
          {fbt('Decline', 'Button turning down a ticket someone assigned to the viewer')}
        </Button>
      </div>
    </div>
  )
}
